import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Resident {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  eligibility_role: string;
  status: string;
  created_at: number;
}

export function Residents() {
  const qc = useQueryClient();
  const list = useQuery<{ residents: Resident[] }>({
    queryKey: ['residents'],
    queryFn: () => api.get('/residents'),
  });

  const [showAdd, setShowAdd] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvOpen, setCsvOpen] = useState(false);

  const addMut = useMutation({
    mutationFn: (body: { name: string; email: string; phone?: string; eligibilityRole: string }) =>
      api.post('/residents', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['residents'] }); setShowAdd(false); },
  });

  const importMut = useMutation({
    mutationFn: (rows: Array<Record<string, string>>) => api.post('/residents/import', { rows }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['residents'] }); setCsvOpen(false); setCsvText(''); },
  });

  function parseCsv(text: string): Array<Record<string, string>> {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const headers = lines[0]!.split(',').map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cells = line.split(',').map((c) => c.trim());
      const row: Record<string, string> = {};
      headers.forEach((h, i) => { if (cells[i]) row[h] = cells[i]!; });
      return row;
    });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Residents</h1>
        <div className="flex gap-2">
          <button onClick={() => setCsvOpen(true)} className="px-3 py-1.5 border border-slate-300 rounded text-sm hover:bg-slate-100">Import CSV</button>
          <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 bg-brand text-white rounded text-sm hover:bg-brand-600">Add resident</button>
        </div>
      </div>

      {list.isLoading && <p className="text-slate-500">Loading...</p>}
      {list.error && <p className="text-red-600">Error loading residents</p>}
      {list.data && (
        <div className="bg-white border border-slate-200 rounded">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.data.residents.map((r) => (
                <tr key={r.id} className="border-b last:border-b-0">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-slate-600">{r.email}</td>
                  <td className="px-3 py-2 text-slate-600">{r.phone ?? '—'}</td>
                  <td className="px-3 py-2">{r.eligibility_role}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{r.status}</span></td>
                </tr>
              ))}
              {list.data.residents.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">No residents yet. Add one or import a CSV.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title="Add resident">
          <AddResidentForm onSubmit={(v) => addMut.mutate(v)} loading={addMut.isPending} error={addMut.error?.message ?? null} />
        </Modal>
      )}

      {csvOpen && (
        <Modal onClose={() => setCsvOpen(false)} title="Import residents (CSV)">
          <p className="text-xs text-slate-500 mb-2">Header row required: <code>name,email,phone,lotNumber,eligibilityRole</code> (lotNumber and eligibilityRole optional).</p>
          <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={10}
            className="w-full border border-slate-300 rounded p-2 font-mono text-xs"
            placeholder="name,email,phone,lotNumber,eligibilityRole&#10;Jane Doe,jane@example.com,5045551234,12,owner" />
          {importMut.error && <div className="text-red-600 text-sm mt-2">{importMut.error.message}</div>}
          {importMut.data ? (
            <pre className="text-xs bg-slate-50 p-2 mt-2 rounded">{JSON.stringify(importMut.data, null, 2)}</pre>
          ) : null}
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setCsvOpen(false)} className="px-3 py-1.5 border border-slate-300 rounded text-sm">Cancel</button>
            <button onClick={() => importMut.mutate(parseCsv(csvText))} disabled={importMut.isPending}
              className="px-3 py-1.5 bg-brand text-white rounded text-sm disabled:opacity-50">{importMut.isPending ? 'Importing...' : 'Import'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AddResidentForm({ onSubmit, loading, error }: { onSubmit: (v: { name: string; email: string; phone?: string; eligibilityRole: string }) => void; loading: boolean; error: string | null }) {
  const [v, setV] = useState({ name: '', email: '', phone: '', eligibilityRole: 'owner' });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(v); }} className="space-y-3">
      <Input label="Name" value={v.name} onChange={(x) => setV({ ...v, name: x })} required />
      <Input label="Email" type="email" value={v.email} onChange={(x) => setV({ ...v, email: x })} required />
      <Input label="Phone" value={v.phone} onChange={(x) => setV({ ...v, phone: x })} />
      <label className="block">
        <span className="text-sm font-medium">Eligibility role</span>
        <select value={v.eligibilityRole} onChange={(e) => setV({ ...v, eligibilityRole: e.target.value })}
          className="mt-1 w-full border border-slate-300 rounded px-3 py-2">
          <option value="owner">Owner</option>
          <option value="lessee">Lessee</option>
          <option value="usufructuary">Usufructuary</option>
          <option value="co_owner">Co-owner</option>
        </select>
      </label>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <button type="submit" disabled={loading} className="w-full bg-brand text-white py-2 rounded font-medium disabled:opacity-50">{loading ? 'Saving...' : 'Add'}</button>
    </form>
  );
}

function Input({ label, value, onChange, type = 'text', required }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-slate-300 rounded px-3 py-2" />
    </label>
  );
}

function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
